import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  AiChatData,
  AiChatItemData,
  DracoAIErrors,
  AiChatEvents,
  AiChatItemType,
  AiChatStatus,
  AiChatQuestionsDetail,
} from '../types';
import { TemplateState } from '../../../types/common/types';
import { GzAvailableItemsCardWebComponent } from '../../gzAvailableItemsCard';
import { GzChatCardWebComponent } from '../../gzChatCard';
import { GzChatIncidentUpdateWebComponent } from '../../gzChatIncidentUpdate';
import { GzChatCardState } from '../../gzChatCard/types';
import { GzTextAnimationWebComponent } from '../../../ui/gzTextAnimation';
import { GzTextareaWithSuggestionsWebComponent } from '../../../ui/gzTextareaWithSuggestions';
import { GzInfoBoxWebComponent } from '../../../ui/gzInfoBox';
import { InfoBoxSeverity } from '../../../ui/gzInfoBox/types';
import { translateFn } from '../../../utils/translator.helper';
import { AiChatWebComponent } from '../component/AiChatWebComponent';
import {debounce} from "../../../utils/event.utils";
import {WriteAnimationEvents} from "../../../ui/gzTextAnimation/types";
import {htmlEncode} from "../../../utils/escape.utils";
import { GzChatNoticeWebComponent } from '../../gzChatNotice';

export class AiChatView extends BaseWebComponentView {
  private cachedTextareaErrorText: string;
  private answers: any = [];

  getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$chatContainer = this.shadow.querySelector('#chatContainer');
    this.dom.$emptyStateTitle = this.shadow.querySelector('#emptyStateTitle');
    this.dom.$emptyStateText = this.shadow.querySelector('#emptyStateText');
    this.dom.$errorStateTitle = this.shadow.querySelector('#errorStateTitle');
    this.dom.$errorStateText = this.shadow.querySelector('#errorStateText');
    this.dom.$unavailableStateTitle = this.shadow.querySelector('#unavailableStateTitle');
    this.dom.$unavailableStateText = this.shadow.querySelector('#unavailableStateText');
    this.dom.$chatTextarea = this.shadow.querySelector('#chatTextarea') as GzTextareaWithSuggestionsWebComponent;
    this.dom.$visibleAvailableItemsCard = this.shadow.querySelector(
      '#visibleAvailableItemsCard',
    ) as GzAvailableItemsCardWebComponent;
    this.dom.$hiddenAvailableItemsCard = this.shadow.querySelector(
      '#hiddenAvailableItemsCard',
    ) as GzAvailableItemsCardWebComponent;
    this.dom.$temporarySendButton = this.shadow.querySelector('#temporarySendButton'); // TODO: remove after implementing textarea component

    this.setTranslatedStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$emptyStateTitle.innerHTML = translateFn('Start chatting');
    this.dom.$emptyStateText.innerHTML = translateFn('The answers will be formulated by an automated intelligence tool and are intended to provide specific information regarding the incident. While we strive to deliver accurate and reliable content, there may be limitations and inaccuracies present,<br/>therefore further analysis may be needed before taking suitable decisions regarding your next actions.<br/>The content is provided as-is without any warranties or guarantees of accuracy.<br/>We disclaim any liability for damages or losses resulting from the use or reliance on this content.');
    this.dom.$errorStateTitle.innerHTML = translateFn('Something went wrong');
    this.dom.$errorStateText.innerHTML = translateFn(
      'There was an error in handling your query.<br/>Please try again later.',
    );
    this.dom.$unavailableStateTitle.innerHTML = translateFn('AI Assistant currently not available');

    if (this.dependencies.incident.errorCode === undefined) {
      this.dom.$unavailableStateText.innerHTML = translateFn(
        'Unable to access information from incidents that occurred prior to the activation.',
      );
    } else {
      if (this.dependencies.incident.errorCode === DracoAIErrors.MAX_XRCA_SIZE_EXCEEDED) {
        this.dom.$unavailableStateText.innerHTML = translateFn('Incident context exceeds the maximum allowed context for query.');
      } else if (this.dependencies.incident.errorCode === DracoAIErrors.XRCA_NOT_FOUND_IN_ES) {
        this.dom.$unavailableStateText.innerHTML = translateFn('AI Assistant currently not available. Please contact Technical Support.');
      } else {
        this.dom.$unavailableStateText.innerHTML = translateFn('Please contact Technical Support.');
      }
    }
    this.dom.$visibleAvailableItemsCard.text = this.dom.$hiddenAvailableItemsCard.text =
      translateFn('Available questions');

    this.dom.$visibleAvailableItemsCard.outOfItemsText = this.dom.$hiddenAvailableItemsCard.outOfItemsText =
      translateFn('No questions available');

    this.dom.$chatTextarea.placeholder = translateFn('Type your question here');
    this.dom.$chatTextarea.defaultHintText = translateFn(
      'Press Ctrl + / to open the default questions list.',
    );
    this.dom.$chatTextarea.disabledTextareaHintText = translateFn(
      'Chatbox is currently in use. Another user in your company is typing in a question.',
    );
    this.dom.$chatTextarea.suggestionActionButtonText = translateFn('View answer');
    this.dom.$chatTextarea.maxCharsErrorText = translateFn('Maximum number of characters exceeded');
    this.dom.$chatTextarea.requiredErrorText = translateFn('This field is required');
    this.cachedTextareaErrorText = translateFn(
      'Failed to load response',
    );
  }

  buildSuccessView(data: AiChatData): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.removeChatNoticeFromDom();
    this.answers = [];
    if (!data) {
      this.buildErrorView();
      return;
    } else if (data?.defaultQuestionList) {
      this.dom.$chatTextarea.defaultSuggestionList = data.defaultQuestionList;
    }
    this.dom.$chatTextarea.showSuggestionActionButton = false;
    this.setAvailableQuestionsCardData(data.totalNumberOfQuestions, data.currentNumberOfQuestions);

    if (data.status === AiChatStatus.UNAVAILABLE) {
      this.buildViewWithState(TemplateState.UNAVAILABLE);
      return;
    }

    if (data.status === AiChatStatus.MAX_XRCA_SIZE_EXCEEDED_AFTER_UPDATE) {
      this.disableInput(true, translateFn(
        'Incident context exceeds the maximum allowed context for query.',
      ));
    }

    if (data.status === AiChatStatus.XRCA_NOT_FOUND_IN_ES_AFTER_UPDATE) {
      this.disableInput(true, translateFn(
        'AI Assistant currently not available. Please contact Technical Support.',
      ));
    }

    if (!this.dependencies.availableUserSession) {
      this.dom.$chatTextarea.disabled = true;
      this.dom.$chatTextarea.disabledTextareaHintText = translateFn(
        'Chatbox is currently in use. Another user in your company is typing in a question.'
      );
    }

    if (data.status === AiChatStatus.EMPTY) {
      this.buildViewWithState(TemplateState.EMPTY);
      return;
    }

    if (data.currentNumberOfQuestions < 1) {
      this.handleOutOfItemsEvent();
    }

    this.buildChatView(data).then(() => this.scrollChatToBottom());
  }

  private setAvailableQuestionsCardData(totalNumber: number, currentNumber: number): void {
    this.dom.$visibleAvailableItemsCard.totalNumber = this.dom.$hiddenAvailableItemsCard.totalNumber = totalNumber;
    this.dom.$visibleAvailableItemsCard.currentNumber = this.dom.$hiddenAvailableItemsCard.currentNumber =
      currentNumber;
  }

  buildErrorView(): void {
    this.buildViewWithState(TemplateState.ERROR);
  }

  async addQuestionToChat(message: string) {
    this.resetTemplateState(this.dom.$wrapper);
    this.removeChatNoticeFromDom();
    this.answers.forEach(answer => answer.hasRegenerate = false);

    const chatMessageData = this.createNewChatMessageData({
      message,
      user: {
        userName: this.dependencies.getCurrentUser().userName,
        userId: this.dependencies.getCurrentUser().userId,
        companyId: this.dependencies.getCurrentUser().companyId,
      },
      isQuestion: true,
    });
    const chatCard = this.createLoadingChatCard(chatMessageData, false, true);
    await this.appendChatCard(chatCard);

    try {
      const messages = await this.dependencies.api.clients.createRecords([chatMessageData]);
      const messageRecord = messages.records[0];
      chatCard.timestamp = messageRecord.created;
      chatCard.id = messageRecord._id;
      chatCard.state = GzChatCardState.SUCCESS;
      await this.updateAvailableQuestionsCard();
      return messageRecord;
    } catch (e) {
      chatCard.state = GzChatCardState.ERROR;
      chatCard.hasRegenerate = false;
      this.disableInput(true, translateFn('Failed to load response'));
      throw new Error(e.message);
    }
  }

  private async updateAvailableQuestionsCard(): Promise<void> {
    let questionsDetail: AiChatQuestionsDetail = await this.dependencies.api.clients.getQuestionsDetail();
    if (!questionsDetail) {
      questionsDetail = {
        totalNumberOfQuestions: 0,
        availableNumberOfQuestions: 0
      }
    }
    const currentNumber = questionsDetail.availableNumberOfQuestions;
    this.dom.$visibleAvailableItemsCard.currentNumber = this.dom.$hiddenAvailableItemsCard.currentNumber =
      currentNumber;
  }

  handleOutOfItemsEvent(): void {
    this.setInputError(true, translateFn(
      'Questions depleted. Refill your questions to continue.',
    ));
    this.disableInput(true, translateFn(
      'Questions depleted. Refill your questions to continue.',
    ));
  }

  handleHasAvailableItems(): void {
    this.setInputError(false, this.cachedTextareaErrorText);
  }

  private setInputError(hasErrorState: boolean, errorText: string): void {
    this.dom.$chatTextarea.hasErrorState = hasErrorState;
    this.dom.$chatTextarea.errorText = errorText;
  }

  disableFeedbackButton(chatCardId: string) {
    for (const chatItem of this.dom.$chatContainer.childNodes) {
      if (chatItem.id === chatCardId) {
        const chatCard = chatItem as GzChatCardWebComponent;
        chatCard.hasFeedback = true;
      }
    }
  }

  async regenerateResponse(chatCardId: string): Promise<void> {
    this.disableInput(true, translateFn(
      'Regenerating answer',
    ));
    for (const chatCard of this.answers) {
      if (chatCard.id === chatCardId) {
        chatCard.state = GzChatCardState.LOADING;
        try {
          const regeneratedAnswer = await this.dependencies.api.clients.regenerateAnswer(chatCardId);
          if (!regeneratedAnswer) {
            chatCard.timestamp = new Date().getTime();
            await this.onChatCardError(chatCard);
          } else {
            chatCard.timestamp = regeneratedAnswer.created;
            chatCard.hasFeedback = false;
            chatCard.id = regeneratedAnswer._id;
          }
          await this.updateAvailableQuestionsCard();

          if (regeneratedAnswer.errorState) {
            await this.onChatCardError(chatCard);
          } else {
            await this.onChatCardSuccess(chatCard, regeneratedAnswer.message);
          }
        } catch (e) {
          await this.onChatCardError(chatCard);
          this.disableInput(true, translateFn('Failed to load response'));
          throw new Error(e.message);
        }
      }
    }
  }

  disableInput(disabled: boolean = true, disabledTextareaHintText: string = ''): void {
    if (this.dom.$chatTextarea) {
      this.dom.$chatTextarea.disabled = disabled;
      this.dom.$chatTextarea.disabledTextareaHintText = disabledTextareaHintText;
    }
  }

  scrollToAnswer(question: string): void {
    const childElement = this.dom.$chatContainer.querySelector('[message="' + question + '"]');
    if (childElement) {
      const chatCardHeaderHeight = 30;
      this.dom.$chatContainer.scrollTo({
        top: childElement.offsetTop - chatCardHeaderHeight,
        behavior: 'smooth',
      });
    }
  }

  private buildViewWithState(templateState: TemplateState): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.removeChatNoticeFromDom();
    this.answers = [];
    this.setTemplateState(this.dom.$wrapper, templateState);
  }

  private async buildChatView(data: AiChatData): Promise<void> {
    const chatItems: AiChatItemData[] = data.chatItems;

    let chatMessages = this.dom.$chatContainer.querySelector('gz-chat-card');
    while (chatMessages) {
      chatMessages.remove();
      chatMessages = this.dom.$chatContainer.querySelector('gz-chat-card');
    }

    let chatUpdates = this.dom.$chatContainer.querySelector('gz-chat-incident-update');
    while (chatUpdates) {
      chatUpdates.remove();
      chatUpdates = this.dom.$chatContainer.querySelector('gz-chat-incident-update');
    }

    for (let index = 0; index < chatItems.length; index++) {
      const chatItem = chatItems[index];
      if (chatItem.type === AiChatItemType.CHAT_CARD) {
        const webComponent = chatItem.errorState
          ? GzChatCardWebComponent.fromAiChatItem({
            ...chatItem,
            state: GzChatCardState.ERROR,
          })
          : GzChatCardWebComponent.fromAiChatItem(chatItem, false, true);
        this.dom.$chatContainer.appendChild(webComponent);
        if (chatItem.errorState) {
          this.disableInput(true, translateFn('Failed to load response'));
        }
        this.answers.push(webComponent);
        if (index === chatItems.length - 1 && chatItem.isQuestion) {
          // Add loading chat item if no answer is present
          const chatCard = await this.appendLoadingAnswerChatCard();
          this.answers.push(chatCard);
          // Disable input if the last item in the conversation is a question
          this.disableInput(true, translateFn('Request in progress. Please wait or refresh the page.'));
        }

        if (!this.dependencies.availableUserSession) {
          webComponent.hasFeedback = true;
        }
      } else {
        const webComponent = GzChatIncidentUpdateWebComponent.fromAiChatItem(chatItem);
        this.dom.$chatContainer.appendChild(webComponent);
      }
    }

    const lastMessage = this.answers.at(-1);
    lastMessage.hasRegenerate = !lastMessage.isQuestion && this.dependencies.availableUserSession;

    let infoBoxComp = this.dom.$chatContainer.querySelector('gz-info-box');
    while (infoBoxComp) {
      infoBoxComp.remove();
      infoBoxComp = this.dom.$chatContainer.querySelector('gz-info-box');
    }

    if (data.status === AiChatStatus.MAX_XRCA_SIZE_EXCEEDED_AFTER_UPDATE) {
      const infoBox = this.buildCustomInfoBox(InfoBoxSeverity.WARNING, translateFn('Incident context exceeds the maximum allowed context for query.'));
      this.dom.$chatContainer.appendChild(infoBox);
    } else if (data.status === AiChatStatus.XRCA_NOT_FOUND_IN_ES_AFTER_UPDATE) {
      const infoBox = this.buildCustomInfoBox(InfoBoxSeverity.WARNING, translateFn('AI Assistant currently not available. Please contact Technical Support.'));
      this.dom.$chatContainer.appendChild(infoBox);
    }

    if (!this.dependencies.availableUserSession) {
      const infoBox = this.buildCustomInfoBox(InfoBoxSeverity.WARNING, translateFn('Chatbox is currently in use. Another user in your company is typing in a question.'));
      this.dom.$chatContainer.appendChild(infoBox);
    }

    this.setChatNoticeToLastResponse();
  }

  private async appendLoadingAnswerChatCard() {
    const chatMessageData = this.createNewChatMessageData({
      user: {
        userName: 'AI Assistant',
      },
      isQuestion: false,
    });
    const chatCard = this.createLoadingChatCard(chatMessageData, true, false);
    await this.appendChatCard(chatCard);
    return chatCard;
  }

  private createNewChatMessageData(options: Partial<AiChatItemData>): Partial<AiChatItemData> {
    return {
      type: AiChatItemType.CHAT_CARD,
      incident: this.dependencies.incident,
      companyId: this.dependencies.getActiveCompany().companyId,
      ...options,
    };
  }

  private createLoadingChatCard(chatMessageData, hasRegenerate, hideAnimation): GzChatCardWebComponent {
    const completeChatMessageData: AiChatItemData = {
      ...chatMessageData,
      state: GzChatCardState.LOADING,
    };
    return GzChatCardWebComponent.fromAiChatItem(
      {
        ...completeChatMessageData,
      },
      hasRegenerate,
      hideAnimation,
    );
  }

  private buildCustomInfoBox(severity, innerHTML) {
    const infoBox = new GzInfoBoxWebComponent();
    infoBox.severity = severity;
    infoBox.innerHTML = innerHTML;
    return infoBox;
  }

  async scrollChatToBottom(): Promise<void> {
    if (this.dom.$chatContainer) {
      this.dom.$chatContainer.scrollTop = this.dom.$chatContainer.scrollHeight - this.dom.$chatContainer.clientHeight;
    }
  }

  private async appendChatCard(chatCard: GzChatCardWebComponent): Promise<void> {
    this.dom.$chatContainer.appendChild(chatCard);
    this.setChatNoticeToLastResponse();
    await this.scrollChatToBottom();
  }

  public async addResponseToChat(record): Promise<void> {
    const chatCard = await this.appendLoadingAnswerChatCard();
    this.answers.push(chatCard);
    try {
      const answer = await this.dependencies.api.clients.getAnswer(record);
      if (!answer) {
        chatCard.timestamp = new Date().getTime();
        await this.onChatCardError(chatCard);
      } else {
        this.refreshChat();
        chatCard.timestamp = answer.created;
        chatCard.id = answer._id;
      }

      if (answer.errorState) {
        await this.onChatCardError(chatCard);
      } else {
        await this.onChatCardSuccess(chatCard, answer.message);
      }
    } catch (e) {
      await this.onChatCardError(chatCard);
      this.disableInput(true, translateFn('Failed to load response'));
      throw new Error(e.message);
    }
  }

  /**
   * Render chat again with new data
   * @private
   */
  private refreshChat(): void {
    // Only do this if chat page was entered again after leaving
    // This causes the component to disconnect and the dom variable to be empty
    if (!Object.keys(this.dom).length) {
      const chatComponent = this.getChatComponent();
      if (chatComponent) {
        // Set this to true to force rendering
        chatComponent.propertiesSet = true;
        const view = chatComponent.controller?.view as AiChatView
        // Enable input again
        view.disableInput(false);
      }
    }
  }

  /**
   * Returns current instance of the DracoAI chat component
   * @returns {AiChatWebComponent}
   */
  public getChatComponent(): AiChatWebComponent {
    return document.querySelector('gz-ai-chat') as AiChatWebComponent;
  }

  private async onChatCardError(chatCard: GzChatCardWebComponent): Promise<void> {
    chatCard.state = GzChatCardState.ERROR;
    this.dom.$chatContainer.dispatchEvent(new CustomEvent(AiChatEvents.LOAD_RESPONSE_FAILED));
    await this.scrollChatToBottom();
  }

  private async onChatCardSuccess(chatCard: GzChatCardWebComponent, message: string): Promise<void> {
    const newAnimationComponent = new GzTextAnimationWebComponent();
    newAnimationComponent.message = htmlEncode(message);
    chatCard.innerHTML = '';
    chatCard.appendChild(newAnimationComponent);
    chatCard.state = GzChatCardState.SUCCESS;

    if (this.dom.$visibleAvailableItemsCard.currentNumber !== 0) {
      this.disableInput(false);
    }

    this.scrollChatForAnimationInProgress(chatCard, newAnimationComponent);

  }

  private scrollChatForAnimationInProgress(chatCard, newAnimationComponent) {
    const resizeObserver = new ResizeObserver(debounce(this.scrollChatToBottom.bind(this), 100));

    newAnimationComponent.addEventListener(WriteAnimationEvents.STARTED, () => {
      resizeObserver.observe(chatCard);
    })

    newAnimationComponent.addEventListener(WriteAnimationEvents.FINISHED, () => {
      resizeObserver.unobserve(chatCard);
    });
  }

  private removeChatNoticeFromDom() {
    const existingChatNotice = this.dom.$chatNotice ?? undefined;

    if (existingChatNotice) {
      existingChatNotice.remove();
    }
  }

  private setChatNoticeToLastResponse() {
    // using chat container, since we need to add the banner under the last created child element
    const lastElement = this.dom.$chatContainer.lastElementChild;

    // this banner should not appear under a question
    if (lastElement === undefined || lastElement.isQuestion) {
      return;
    }

    const message = translateFn('Please note that the answers are formulated by an automatic artificial intelligence tool.');
    const chatNotice = new GzChatNoticeWebComponent();
    chatNotice.message = message;
    this.dom.$chatNotice = chatNotice;
    this.dom.$chatContainer.appendChild(chatNotice);
  }
}
